import type { ParseKeys } from "i18next";

import {
  ADDRESS_APARTMENT_NUMBER_MAX_LENGTH,
  ADDRESS_STREET_NAME_MAX_LENGTH,
  ADDRESS_STREET_NUMBER_MAX_LENGTH,
  CITY_OPTIONS,
  EMPLOYER_NAME_MAX_LENGTH,
  formatZip,
  GROSS_NET_OPTIONS,
  INCOME_TYPE_OPTIONS,
  OCCUPATION_MAX_LENGTH,
  PAY_FREQUENCY_OPTIONS,
  US_STATE_OPTIONS,
  ZIP_PASTE_MAX_LENGTH,
} from "./constants";
import type { IncomeInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// Field kind discriminated union — used by IncomeBlock to render correctly
// ---------------------------------------------------------------------------
// Select options carry a resolved display label (built in constants.ts — i18n
// option labels are translated there; states/cities are literal reference data).
type SelectOption = { value: string; label: string };

type BaseField = {
  name: keyof IncomeInput;
  /** i18n key (resolved via `t()` in IncomeBlock), not display text. */
  label: ParseKeys;
  required?: boolean;
  /** i18n key for the placeholder shown inside the input. */
  placeholder?: ParseKeys;
  /** Visually hide the label (kept for screen readers via sr-only). */
  hideLabel?: boolean;
  /** Extra classes for the input/select control (e.g. a fixed width). */
  controlClassName?: string;
  /** Extra classes for the field label (e.g. whitespace-nowrap). */
  labelClassName?: string;
  /** Extra classes for the FormItem wrapper (e.g. label-left layout at lg). */
  itemClassName?: string;
};

type TextField = BaseField & {
  kind: "text" | "phone" | "date";
  /** Transforms the raw typed/pasted value before it reaches RHF (e.g. ZIP+4 dash formatting). */
  format?: (value: string) => string;
  /**
   * Native maxLength — when `format` is set, this must be generous enough to
   * cover a punctuated paste (e.g. "90210-1234 extra"), since the browser
   * truncates the raw value BEFORE `format` runs. `format` itself is
   * responsible for producing the correctly-truncated final value.
   */
  maxLength?: number;
  inputMode?: "numeric" | "text";
  /** Uppercases the value on blur/tab-out — matches dealer-ui behavior for employer/address fields. */
  uppercase?: boolean;
};
type NumberField = BaseField & { kind: "number" };
type TextareaField = BaseField & { kind: "textarea" };
type SelectField = BaseField & {
  kind: "select";
  options: readonly SelectOption[];
  /** Suppress the "Select <label>" placeholder (e.g. when the field has a default). */
  hidePlaceholder?: boolean;
};
type CheckboxField = BaseField & { kind: "checkbox"; checkLabel: string };

export type IncomeFieldConfig = TextField | NumberField | TextareaField | SelectField | CheckboxField;

/** Config kinds FieldRenderer dispatches on directly — checkbox ("differentWorkLocation") is rendered by its own hardcoded block instead. */
export type RenderableFieldConfig = Exclude<IncomeFieldConfig, CheckboxField>;

// ---------------------------------------------------------------------------
// Ordered list of all income fields
// ---------------------------------------------------------------------------
// `label`/`placeholder`/`checkLabel` hold i18n keys (resolved with `t()` in
// IncomeBlock), not display strings. Copy lives in src/languages/en.json.
export const INCOME_FIELDS: IncomeFieldConfig[] = [
  {
    kind: "select",
    name: "employmentType",
    label: "review.fields.incomeType",
    required: true,
    // Full list is the fallback; IncomeBlock overrides these options at render
    // time with getIncomeTypeOptions(applicantSsnType) to gate them by the
    // applicant's SSN/ITIN status (OR-21950).
    options: INCOME_TYPE_OPTIONS,
  },
  {
    kind: "text",
    name: "employerName",
    label: "review.fields.employerName",
    required: true,
    maxLength: EMPLOYER_NAME_MAX_LENGTH,
    uppercase: true,
  },
  {
    kind: "text",
    name: "occupation",
    label: "review.fields.occupation",
    maxLength: OCCUPATION_MAX_LENGTH,
    uppercase: true,
  },
  {
    kind: "text",
    name: "streetNumber",
    label: "review.fields.streetNumber",
    placeholder: "review.fields.streetNumber",
    hideLabel: true,
    maxLength: ADDRESS_STREET_NUMBER_MAX_LENGTH,
    uppercase: true,
  },
  {
    kind: "text",
    name: "streetName",
    label: "review.fields.streetName",
    placeholder: "review.fields.streetName",
    hideLabel: true,
    maxLength: ADDRESS_STREET_NAME_MAX_LENGTH,
    uppercase: true,
  },
  {
    kind: "text",
    name: "apartmentNumber",
    label: "review.fields.suite",
    placeholder: "review.fields.suite",
    hideLabel: true,
    maxLength: ADDRESS_APARTMENT_NUMBER_MAX_LENGTH,
    uppercase: true,
  },
  {
    kind: "text",
    name: "zip",
    label: "review.fields.zip",
    format: formatZip,
    maxLength: ZIP_PASTE_MAX_LENGTH,
    inputMode: "numeric",
  },
  {
    kind: "select",
    name: "city",
    label: "review.fields.city",
    options: CITY_OPTIONS,
  },
  {
    kind: "select",
    name: "state",
    label: "review.fields.state",
    options: US_STATE_OPTIONS,
  },
  // verificationPhone + verificationPhoneExt are rendered together by the
  // dedicated PhoneExtField component (label-left at lg, grouped inputs), not
  // via the generic FieldRenderer — so they intentionally carry no layout props.
  {
    kind: "phone",
    name: "employerPhone",
    label: "review.fields.verificationPhone",
    required: true,
  },
  {
    kind: "text",
    name: "phoneExtension",
    label: "review.fields.verificationPhoneExt",
  },
  {
    kind: "checkbox",
    name: "differentWorkLocation",
    label: "review.fields.workLocation",
    checkLabel: "review.income.differentWorkLocation",
  },
  {
    kind: "number",
    name: "monthlyGrossPayAmount",
    label: "review.fields.grossMonthlyPay",
    required: true,
    controlClassName: "w-32",
  },
  {
    kind: "date",
    name: "hireDate",
    label: "review.fields.hireDate",
    required: true,
    controlClassName: "w-32",
  },
  {
    kind: "number",
    name: "dscEnteredMonthlyIncomePerPayPeriod",
    label: "review.fields.payPerPeriod",
    controlClassName: "w-32",
  },
  {
    kind: "select",
    name: "incomeFrequencyType",
    label: "review.fields.payFrequency",
    options: PAY_FREQUENCY_OPTIONS,
    controlClassName: "w-36",
    hidePlaceholder: true,
  },
  {
    kind: "select",
    name: "payType",
    label: "review.fields.grossNet",
    options: GROSS_NET_OPTIONS,
    controlClassName: "w-32",
  },
  {
    kind: "textarea",
    name: "notes",
    label: "review.fields.additionalInformation",
    // single-row height (override the textarea's default min-h-14). min-h-9
    // (not min-h-0) matches the height field-sizing:content settles at once
    // text is entered, so the box doesn't jump/reflow on the first keystroke.
    controlClassName: "min-h-9",
  },
];
